import React from "react";
import { Link, useParams } from "react-router-dom";
import { blogPosts } from "../blogData";
import { forSaleListings } from "../mock";
import PropertyCard from "../components/heider/PropertyCard";
import SwipeRow from "../components/heider/SwipeRow";
import Footer from "../components/heider/Footer";
import ContactForm from "../components/heider/ContactForm";
import useReveal from "../hooks/useReveal";
import { ArrowLeft, ArrowRight } from "lucide-react";

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);
  useReveal(slug);

  if (!post) {
    return (
      <div className="page-in pt-40 pb-32 text-center">
        <p className="font-serif-display text-3xl text-[#1a1a1a] mb-4">Article not found</p>
        <Link to="/blog" className="text-[11px] tracking-luxe uppercase text-[#b4936a] border-b border-[#b4936a] pb-1">Back to the Journal</Link>
        <Footer />
      </div>
    );
  }

  const related = (post.relatedIds || []).map((id) => forSaleListings.find((l) => l.id === id)).filter(Boolean);
  const more = blogPosts.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <div className="page-in">
      <article>
        <section className="relative pt-36 pb-16 md:pt-44 md:pb-20 bg-[#0d0d0d] text-white overflow-hidden">
          <div className="absolute inset-0 opacity-30">
            <img src={post.cover} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-[#0d0d0d]/70" />
          </div>
          <div className="relative max-w-[860px] mx-auto px-6 md:px-10">
            <Link to="/blog" className="inline-flex items-center gap-2 text-[11px] tracking-luxe uppercase text-white/60 hover:text-[#b4936a] transition-colors mb-8"><ArrowLeft size={14} /> The Journal</Link>
            <p className="text-[11px] tracking-luxe uppercase text-[#b4936a] mb-5">{post.category} &middot; {post.date} &middot; {post.readTime}</p>
            <h1 className="font-serif-display text-4xl md:text-6xl font-light leading-tight">{post.title}</h1>
          </div>
        </section>

        <section className="bg-white py-16 md:py-24">
          <div className="max-w-[760px] mx-auto px-6 md:px-10">
            <p className="font-serif-display text-2xl md:text-[1.7rem] leading-relaxed text-[#2a2a2a] font-light mb-12 reveal">{post.excerpt}</p>
            {post.sections.map((s, i) => (
              <div key={i} className="mb-10 reveal">
                <h2 className="font-serif-display text-2xl md:text-3xl font-light text-[#1a1a1a] mb-4">{s.h}</h2>
                {s.p.map((para, j) => (
                  <p key={j} className="text-neutral-600 text-[16px] leading-[1.9] mb-4">{para}</p>
                ))}
              </div>
            ))}
            <div className="mt-12 pt-8 border-t border-black/10 flex flex-wrap gap-2">
              {post.keywords.map((k) => (
                <span key={k} className="text-[10px] tracking-luxe uppercase text-neutral-500 bg-[#f3f1ec] rounded-full px-4 py-1.5">{k}</span>
              ))}
            </div>
          </div>
        </section>

        {related.length > 0 && (
          <section className="bg-[#f3f1ec] py-20 md:py-24">
            <div className="max-w-[1500px] mx-auto px-6 md:px-10">
              <p className="text-[11px] tracking-luxe uppercase text-[#b4936a] mb-3">Featured in this Area</p>
              <h2 className="font-serif-display text-3xl md:text-4xl font-light text-[#1a1a1a] mb-10">Explore These Listings</h2>
              <div className="reveal">
                <SwipeRow testid="blog-related-swipe">
                  {related.map((item) => <PropertyCard key={item.id} item={item} />)}
                </SwipeRow>
              </div>
            </div>
          </section>
        )}

        <section className="bg-white py-20 md:py-24">
          <div className="max-w-[1200px] mx-auto px-6 md:px-10">
            <h2 className="font-serif-display text-3xl md:text-4xl font-light text-[#1a1a1a] mb-10">Keep Reading</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {more.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="group">
                  <div className="aspect-[4/3] overflow-hidden bg-neutral-200 mb-4">
                    <img src={p.cover} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                  </div>
                  <p className="text-[10px] tracking-luxe uppercase text-[#b4936a] mb-2">{p.category}</p>
                  <h3 className="font-serif-display text-xl text-[#1a1a1a] group-hover:text-[#b4936a] transition-colors leading-snug">{p.title}</h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </article>
      <ContactForm />
      <Footer />
    </div>
  );
};

export default BlogPost;
